import { 
  collection, 
  doc, 
  getDoc, 
  setDoc, 
  updateDoc, 
  query, 
  where, 
  getDocs,
  Timestamp 
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { getISOWeek, getYear } from 'date-fns';
import { HabitMetrics, HabitYearlyHistory, HabitMetricsDisplay, MonthlyMetrics } from '../types';

export class HabitMetricsService {
  
  /**
   * Inicializa as métricas para um novo hábito
   */
  static async initializeMetrics(userId: string, habitId: string): Promise<void> {
    const now = new Date();
    const year = getYear(now);
    const month = now.getMonth() + 1;
    const week = `${year}-W${getISOWeek(now).toString().padStart(2, '0')}`;
    
    const initialMetrics: HabitMetrics = {
      id: `${habitId}_metrics`,
      userId,
      habitId,
      
      // Acumulados
      totalSum: 0,
      totalCount: 0,
      totalAverage: 0,
      
      // Ano atual
      yearSum: 0,
      yearCount: 0,
      yearAverage: 0,
      
      // Mês atual
      monthSum: 0,
      monthCount: 0,
      monthAverage: 0,
      
      // Semana atual
      weekSum: 0,
      weekCount: 0,
      weekAverage: 0,
      
      // Controle temporal
      currentYear: year,
      currentMonth: month,
      currentWeek: week,
      
      lastUpdated: now,
      createdAt: now
    };

    await setDoc(doc(db, 'habitMetrics', initialMetrics.id), {
      ...initialMetrics,
      lastUpdated: Timestamp.fromDate(initialMetrics.lastUpdated),
      createdAt: Timestamp.fromDate(initialMetrics.createdAt)
    });
  }

  /**
   * Atualiza as métricas quando um hábito é marcado como concluído
   */
  static async addCompletion(
    userId: string, 
    habitId: string, 
    value: number, 
    completionDate: Date = new Date()
  ): Promise<void> {
    const metricsId = `${habitId}_metrics`;
    const metricsRef = doc(db, 'habitMetrics', metricsId);
    
    // Buscar métricas atuais
    const metricsDoc = await getDoc(metricsRef);
    let metrics: HabitMetrics;
    
    if (!metricsDoc.exists()) {
      // Se não existir, inicializar
      await this.initializeMetrics(userId, habitId);
      const newMetricsDoc = await getDoc(metricsRef);
      metrics = { ...newMetricsDoc.data(), id: metricsId } as HabitMetrics;
    } else {
      metrics = { ...metricsDoc.data(), id: metricsId } as HabitMetrics;
    }

    // Verificar se mudou de período (ano, mês, semana)
    const now = completionDate;
    const year = getYear(now);
    const month = now.getMonth() + 1;
    const week = `${year}-W${getISOWeek(now).toString().padStart(2, '0')}`;

    // Se mudou de ano, arquivar dados do ano anterior
    if (year > metrics.currentYear) {
      await this.archiveYearData(userId, habitId, metrics);
      // Reset dados anuais
      metrics.yearSum = 0;
      metrics.yearCount = 0;
      metrics.yearAverage = 0;
      metrics.currentYear = year;
    }

    // Se mudou de mês, reset dados mensais
    if (month !== metrics.currentMonth) {
      metrics.monthSum = 0;
      metrics.monthCount = 0;
      metrics.monthAverage = 0;
      metrics.currentMonth = month;
    }

    // Se mudou de semana, reset dados semanais
    if (week !== metrics.currentWeek) {
      metrics.weekSum = 0;
      metrics.weekCount = 0;
      metrics.weekAverage = 0;
      metrics.currentWeek = week;
    }

    // Atualizar métricas com o novo valor
    metrics.totalSum += value;
    metrics.totalCount += 1;
    metrics.totalAverage = metrics.totalSum / metrics.totalCount;

    metrics.yearSum += value;
    metrics.yearCount += 1;
    metrics.yearAverage = metrics.yearSum / metrics.yearCount;

    metrics.monthSum += value;
    metrics.monthCount += 1;
    metrics.monthAverage = metrics.monthSum / metrics.monthCount;

    metrics.weekSum += value;
    metrics.weekCount += 1;
    metrics.weekAverage = metrics.weekSum / metrics.weekCount;

    metrics.lastUpdated = now;

    // Salvar no Firestore
    await updateDoc(metricsRef, {
      totalSum: metrics.totalSum,
      totalCount: metrics.totalCount,
      totalAverage: metrics.totalAverage,
      yearSum: metrics.yearSum,
      yearCount: metrics.yearCount,
      yearAverage: metrics.yearAverage,
      monthSum: metrics.monthSum,
      monthCount: metrics.monthCount,
      monthAverage: metrics.monthAverage,
      weekSum: metrics.weekSum,
      weekCount: metrics.weekCount,
      weekAverage: metrics.weekAverage,
      currentYear: metrics.currentYear,
      currentMonth: metrics.currentMonth,
      currentWeek: metrics.currentWeek,
      lastUpdated: Timestamp.fromDate(metrics.lastUpdated)
    });
  }

  /**
   * Remove uma completion das métricas (quando desmarcado)
   */
  static async removeCompletion(
    userId: string, 
    habitId: string, 
    value: number,
    completionDate: Date = new Date()
  ): Promise<void> {
    const metricsId = `${habitId}_metrics`;
    const metricsRef = doc(db, 'habitMetrics', metricsId);
    
    const metricsDoc = await getDoc(metricsRef);
    if (!metricsDoc.exists()) {
      console.warn('Tentativa de remover completion de métricas inexistentes:', habitId);
      return;
    }

    const metrics = { ...metricsDoc.data(), id: metricsId } as HabitMetrics;

    // Determinar o período da completion removida
    const year = getYear(completionDate);
    const month = completionDate.getMonth() + 1;
    const week = `${year}-W${getISOWeek(completionDate).toString().padStart(2, '0')}`;

    // Atualizar métricas totais
    metrics.totalSum = Math.max(0, metrics.totalSum - value);
    metrics.totalCount = Math.max(0, metrics.totalCount - 1);
    metrics.totalAverage = metrics.totalCount > 0 ? metrics.totalSum / metrics.totalCount : 0;

    // Se a remoção é do período atual, atualizar métricas do período
    if (year === metrics.currentYear) {
      metrics.yearSum = Math.max(0, metrics.yearSum - value);
      metrics.yearCount = Math.max(0, metrics.yearCount - 1);
      metrics.yearAverage = metrics.yearCount > 0 ? metrics.yearSum / metrics.yearCount : 0;

      if (month === metrics.currentMonth) {
        metrics.monthSum = Math.max(0, metrics.monthSum - value);
        metrics.monthCount = Math.max(0, metrics.monthCount - 1);
        metrics.monthAverage = metrics.monthCount > 0 ? metrics.monthSum / metrics.monthCount : 0;

        if (week === metrics.currentWeek) {
          metrics.weekSum = Math.max(0, metrics.weekSum - value);
          metrics.weekCount = Math.max(0, metrics.weekCount - 1);
          metrics.weekAverage = metrics.weekCount > 0 ? metrics.weekSum / metrics.weekCount : 0;
        }
      }
    }

    metrics.lastUpdated = new Date();

    // Salvar no Firestore
    await updateDoc(metricsRef, {
      totalSum: metrics.totalSum,
      totalCount: metrics.totalCount,
      totalAverage: metrics.totalAverage,
      yearSum: metrics.yearSum,
      yearCount: metrics.yearCount,
      yearAverage: metrics.yearAverage,
      monthSum: metrics.monthSum,
      monthCount: metrics.monthCount,
      monthAverage: metrics.monthAverage,
      weekSum: metrics.weekSum,
      weekCount: metrics.weekCount,
      weekAverage: metrics.weekAverage,
      lastUpdated: Timestamp.fromDate(metrics.lastUpdated)
    });
  }

  /**
   * Arquiva os dados do ano anterior
   */
  private static async archiveYearData(
    userId: string, 
    habitId: string, 
    metrics: HabitMetrics
  ): Promise<void> {
    const yearlyHistoryId = `${habitId}_${metrics.currentYear}`;
    
    // TODO: Implementar breakdown mensal do ano
    const monthlyBreakdown: MonthlyMetrics[] = [];
    
    const yearlyHistory: HabitYearlyHistory = {
      id: yearlyHistoryId,
      userId,
      habitId,
      year: metrics.currentYear,
      yearSum: metrics.yearSum,
      yearCount: metrics.yearCount,
      yearAverage: metrics.yearAverage,
      monthlyBreakdown,
      createdAt: new Date()
    };

    await setDoc(doc(db, 'habitYearlyHistory', yearlyHistoryId), {
      ...yearlyHistory,
      createdAt: Timestamp.fromDate(yearlyHistory.createdAt)
    });
  }

  /**
   * Busca as métricas de um hábito para exibição
   */
  static async getMetricsDisplay(userId: string, habitId: string): Promise<HabitMetricsDisplay | null> {
    const metricsId = `${habitId}_metrics`;
    const metricsDoc = await getDoc(doc(db, 'habitMetrics', metricsId));
    
    if (!metricsDoc.exists()) {
      return null;
    }

    const metrics = { ...metricsDoc.data(), id: metricsId } as HabitMetrics;

    // Buscar histórico de anos anteriores
    const historyQuery = query(
      collection(db, 'habitYearlyHistory'),
      where('userId', '==', userId),
      where('habitId', '==', habitId)
    );
    
    const historySnapshot = await getDocs(historyQuery);
    const yearlyHistory = historySnapshot.docs.map(doc => {
      const data = doc.data() as HabitYearlyHistory;
      return {
        year: data.year,
        sum: data.yearSum,
        average: data.yearAverage,
        count: data.yearCount
      };
    }).sort((a, b) => b.year - a.year); // Mais recentes primeiro

    return {
      year: {
        sum: metrics.yearSum,
        average: metrics.yearAverage,
        count: metrics.yearCount
      },
      month: {
        sum: metrics.monthSum,
        average: metrics.monthAverage,
        count: metrics.monthCount
      },
      week: {
        sum: metrics.weekSum,
        average: metrics.weekAverage,
        count: metrics.weekCount
      },
      yearlyHistory
    };
  }

  /**
   * Recalcula todas as métricas baseadas no histórico completo
   * Usado para correção de dados ou migração
   */
  static async recalculateMetrics(userId: string, habitId: string): Promise<void> {
    // TODO: Implementar recálculo completo baseado em todas as completions
    console.log('Recálculo de métricas não implementado ainda');
  }
}
